// ──────────────────────────────────────────────────────────────────────────
// LETTURA DI UNA TABELLA INCOLLATA
//
// L'elenco degli allievi arriva da un foglio di calcolo, e da un foglio di
// calcolo può arrivare in tre modi: incollato (colonne separate da tabulazione),
// salvato in CSV all'italiana (punto e virgola) o all'inglese (virgola). Non
// chiediamo all'utente quale sia: si guarda la prima riga e si sceglie il
// separatore che la divide in più pezzi.
// ──────────────────────────────────────────────────────────────────────────
const DELIMITERS = ["\t", ";", ","]

const countOutsideQuotes = (line, delimiter) => {
	let count = 0
	let inQuotes = false

	for (let i = 0; i < line.length; i++) {
		if (line[i] === '"') inQuotes = !inQuotes
		else if (line[i] === delimiter && !inQuotes) count++
	}

	return count
}

export const detectDelimiter = text => {
	const firstLine = text.split(/\r?\n/)[0] ?? ""

	return DELIMITERS.reduce(
		(best, delimiter) =>
			countOutsideQuotes(firstLine, delimiter) > countOutsideQuotes(firstLine, best)
				? delimiter
				: best,
		DELIMITERS[DELIMITERS.length - 1],
	)
}

// Le virgolette vanno rispettate: un indirizzo con la virgola dentro è una
// cella sola, e senza questo diventerebbe due colonne sfasando tutta la riga.
export const parseDelimited = (text, delimiter = detectDelimiter(text)) => {
	const rows = []
	let row = []
	let value = ""
	let inQuotes = false

	const endValue = () => {
		row.push(value.trim())
		value = ""
	}

	const endRow = () => {
		endValue()
		if (row.some(cell => cell !== "")) rows.push(row)
		row = []
	}

	for (let i = 0; i < text.length; i++) {
		const char = text[i]

		if (inQuotes) {
			// due virgolette di fila sono una virgoletta vera dentro il testo
			if (char === '"' && text[i + 1] === '"') {
				value += '"'
				i++
			} else if (char === '"') inQuotes = false
			else value += char
			continue
		}

		if (char === '"') inQuotes = true
		else if (char === delimiter) endValue()
		else if (char === "\n") endRow()
		else if (char !== "\r") value += char
	}

	endRow()

	if (rows.length === 0) return { headers: [], rows: [] }

	return { headers: rows[0], rows: rows.slice(1) }
}

// ──────────────────────────────────────────────────────────────────────────
// A QUALE CAMPO CORRISPONDE UNA COLONNA
//
// I fogli veri hanno intestazioni fatte a modo loro — "Cognome", "Nomignolo",
// "Allievo" — quindi si confronta senza spazi, accenti e punteggiatura, e
// basta che l'intestazione contenga uno degli alias.
//
// Si importa solo come si chiama l'allievo. Le altre colonne del foglio —
// telefono, data di nascita, codice fiscale, certificato — restano nel foglio:
// l'app non le chiede più da nessuna parte, e non è il caso che entrino
// proprio dalla porta di servizio.
// ──────────────────────────────────────────────────────────────────────────
export const IMPORT_FIELDS = [
	{ field: "fullName", label: "Nome e cognome", aliases: ["nomeecognome", "nominativo", "allievo", "giocatore"] },
	{ field: "playerName", label: "Nome", aliases: ["nome", "name", "firstname"] },
	{ field: "playerSurname", label: "Cognome", aliases: ["cognome", "surname", "lastname"] },
	{ field: "nickname", label: "Soprannome", aliases: ["soprannome", "nickname", "nomignolo"] },
]

const normalizeHeader = header =>
	(header ?? "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]/g, "")

export const guessFields = headers => {
	const normalized = headers.map(normalizeHeader)
	const fields = new Array(headers.length).fill(null)
	const used = new Set()

	const assign = matches => {
		normalized.forEach((header, index) => {
			if (!header || fields[index]) return

			const match = IMPORT_FIELDS.find(
				({ field, aliases }) =>
					!used.has(field) && aliases.some(alias => matches(header, alias)),
			)
			if (!match) return

			used.add(match.field)
			fields[index] = match.field
		})
	}

	// Prima le corrispondenze esatte, poi quelle parziali: "Cognome"
	// contiene "nome", e a cercare pezzi subito finirebbe sul campo del nome
	// lasciando la colonna del nome senza casa.
	assign((header, alias) => header === alias)
	assign((header, alias) => header.includes(alias))

	return fields
}

// ──────────────────────────────────────────────────────────────────────────
// DALLE RIGHE AGLI ALLIEVI
// ──────────────────────────────────────────────────────────────────────────
const splitFullName = fullName => {
	const parts = fullName.trim().split(/\s+/)
	return { playerName: parts[0] ?? "", playerSurname: parts.slice(1).join(" ") }
}

export const rowsToPlayers = (rows, fields) =>
	rows
		.map(row => {
			const player = {}

			fields.forEach((field, index) => {
				if (!field) return
				const value = (row[index] ?? "").trim()
				if (value === "") return

				if (field === "fullName") Object.assign(player, splitFullName(value))
				else player[field] = value
			})

			return player
		})
		// senza un modo per chiamarlo non è un allievo: è una riga di totali,
		// o una riga vuota
		.filter(
			player =>
				(player.playerName ?? "").trim() !== "" || (player.nickname ?? "").trim() !== "",
		)
